import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import Switch from "../../../../../components/Inputs/Switch";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { dateTimeFormat } from "../../../../../utils/helpers/date";
import { firstToUpper } from "../../../../../utils/helpers/string";
import { compareVersions, defaultComparePair } from "./compareVersions";
import { useRenderVersionRange, versionStatusColors } from "./VersionsTable";

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(T[\d:.]+Z?)?$/;

// Compare the settings of two versions of a time off plan: only the differing ones by default.
function Compare({ versions }) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const renderRange = useRenderVersionRange();
    const [[idA, idB], setPair] = useState(() => defaultComparePair(versions));
    const [showAll, setShowAll] = useState(false);

    // A version picked earlier may be gone (discarded): fall back to the default pair.
    useEffect(() => {
        const ids = versions.map((version) => version.id);
        if (!ids.includes(idA) || !ids.includes(idB)) {
            setPair(defaultComparePair(versions));
        }
    }, [versions, idA, idB]);

    const versionA = versions.find((version) => version.id === idA);
    const versionB = versions.find((version) => version.id === idB);

    const rows = useMemo(
        () => (versionA && versionB ? compareVersions(versionA.data, versionB.data) : []),
        [versionA, versionB]
    );
    const differentCount = rows.filter((row) => row.different).length;
    const visibleRows = showAll ? rows : rows.filter((row) => row.different);

    const renderLabel = (row) => row.segments
        .map((segment) => (segment.i18n ? firstToUpper(t(segment.i18n)) : segment.text))
        .join(" › ");

    const renderValue = (row, value) => {
        if (value === null || value === undefined) {
            return <span className="text-muted">—</span>;
        }
        if (typeof value === "boolean") {
            return value ? t("yes") : t("no");
        }
        if (row.enum && typeof value === "string") {
            return t(value);
        }
        if (typeof value === "string" && DATE_PATTERN.test(value)) {
            return dateTimeFormat(value, dateFormat);
        }
        if (typeof value === "object") {
            return JSON.stringify(value);
        }
        return String(value);
    };

    const renderOption = (version) => `${renderRange(version).replace(/ - $/, "")} · ${t(version.status)}`;

    if (versions.length < 2) {
        return <p className="text-muted small mb-0">{t("time-off-plan-compare-needs-two-versions")}</p>;
    }

    const renderHeader = (version) => version && (
        <>
            <span className="text-nowrap">{renderRange(version)}</span>
            <span className={`d-block ${versionStatusColors[version.status] || ""}`}>{t(version.status)}</span>
        </>
    );

    return (
        <div>
            <Row className="align-items-end">
                <Col xs={12} md={5}>
                    <FormGroup className="mb-2">
                        <Label className="small mb-1" for="time-off-version-a">{t("time-off-plan-compare-from")}</Label>
                        <Input
                            id="time-off-version-a"
                            type="select"
                            bsSize="sm"
                            value={idA || ""}
                            onChange={(e) => setPair([e.target.value, idB])}
                        >
                            {versions.map((version) => (
                                <option key={version.id} value={version.id}>{renderOption(version)}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>

                <Col xs={12} md={2} className="text-center mb-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title={t("time-off-plan-compare-swap")}
                        onClick={() => setPair([idB, idA])}
                    >
                        <i className="fa-solid fa-right-left" />
                    </button>
                </Col>

                <Col xs={12} md={5}>
                    <FormGroup className="mb-2">
                        <Label className="small mb-1" for="time-off-version-b">{t("time-off-plan-compare-to")}</Label>
                        <Input
                            id="time-off-version-b"
                            type="select"
                            bsSize="sm"
                            value={idB || ""}
                            onChange={(e) => setPair([idA, e.target.value])}
                        >
                            {versions.map((version) => (
                                <option key={version.id} value={version.id}>{renderOption(version)}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>

            <div className="d-flex flex-wrap align-items-center justify-content-between my-2">
                <small className="text-muted mr-3">
                    {t("time-off-plan-compare-differences", { count: differentCount })}
                </small>
                <Switch
                    id="time-off-versions-show-all"
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                >
                    {t("show-all-settings")}
                </Switch>
            </div>

            {idA === idB ? (
                <p className="text-muted small mb-0">{t("time-off-plan-compare-select-two")}</p>
            ) : visibleRows.length === 0 ? (
                <p className="text-muted small mb-0">{t("time-off-plan-compare-no-differences")}</p>
            ) : (
                <div className="table-responsive">
                    <Table size="sm" className="mb-0 small">
                        <thead>
                            <tr>
                                <th style={{ minWidth: 200 }}>{t("setting")}</th>
                                <th style={{ minWidth: 120 }}>{renderHeader(versionA)}</th>
                                <th style={{ minWidth: 120 }}>{renderHeader(versionB)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row) => (
                                <tr key={row.key} className={showAll && row.different ? "table-warning" : undefined}>
                                    <td className="text-wrap">{renderLabel(row)}</td>
                                    <td className="text-wrap">{renderValue(row, row.a)}</td>
                                    <td className={row.different ? "text-wrap font-weight-bold" : "text-wrap"}>
                                        {renderValue(row, row.b)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
}

export default Compare;
